import {
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  Signal,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AppHostService } from "../app-host.service";
import { AppParentService } from "../parent/app-parent.service";

@Directive({
  selector: "[fofDragDrop]",
  standalone: true,
  exportAs: "fofDragDrop",
})
/**
 * Manages host-iframe drag communication for any draggable element.
 *
 * Two modes are supported via the `longPress` input:
 * - `false` (default): drag starts immediately on pointerdown — intended for explicit
 *   drag affordances like a drag handle button.
 * - `true`: drag is triggered after a long-press delay or once movement exceeds a pixel
 *   threshold. A press that ends before dragging begins emits `openRequested` — intended
 *   for avatar/icon elements that double as both a tap-to-open and a drag handle.
 *
 * Once a drag session starts, all pointer movement tracking is delegated to the host-side
 * `DragService` via a full-viewport overlay — this directive only handles drag initiation.
 */
export class DragDropDirective {
  private readonly longPressDelayMilliseconds = 200;
  private readonly movementThresholdPixels = 5;

  private readonly hostService = inject(AppHostService);
  private readonly appParentService = inject(AppParentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  /**
   * When true, drag activation is deferred until a long-press delay or movement threshold
   * is reached. A short tap emits `openRequested` instead.
   * When false (default), drag begins immediately on pointerdown.
   */
  @Input() longPress = false;

  /** Emitted when `longPress` is true and a press ends before a drag was initiated. */
  @Output() readonly openRequested = new EventEmitter<void>();

  private readonly _isDragging = signal(false);
  /** Whether a drag session is currently active. Bind to this in host templates. */
  public readonly isDragging: Signal<boolean> = this._isDragging.asReadonly();

  // -- long-press mode state --
  private dragDelayTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerStartCoordinateX: number | null = null;
  private pointerStartCoordinateY: number | null = null;
  private dragStartedFromCurrentPress = false;
  private isPointerPressActive = false;

  private activePointerId: number | null = null;

  constructor() {
    this.hostService.dragEnd$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetDragState());

    this.destroyRef.onDestroy(() => this.clearDragDelayTimer());
  }

  /**
   * Handles pointerdown events on the host element.
   * Starts a drag session immediately or after a long-press delay, depending on the `longPress` input.
   */
  @HostListener("pointerdown", ["$event"])
  protected onPointerDown(pointerEvent: PointerEvent): void {
    this.activePointerId = pointerEvent.pointerId;

    if (this.longPress) {
      const pointerTargetElement = pointerEvent.currentTarget as HTMLElement | null;
      if (pointerTargetElement) {
        pointerTargetElement.setPointerCapture(pointerEvent.pointerId);
      }

      this.isPointerPressActive = true;
      this.dragStartedFromCurrentPress = false;
      this.pointerStartCoordinateX = pointerEvent.clientX;
      this.pointerStartCoordinateY = pointerEvent.clientY;
      this.clearDragDelayTimer();
      this.dragDelayTimer = setTimeout(() => this.startDrag(), this.longPressDelayMilliseconds);
    } else {
      pointerEvent.preventDefault();
      this.startDrag();
    }
  }

  /**
   * Handles pointermove events on the host element.
   * In immediate mode this is a no-op — the host overlay handles all movement tracking.
   * In long-press mode, only checks the movement threshold for drag initiation.
   */
  @HostListener("pointermove", ["$event"])
  protected onPointerMove(pointerEvent: PointerEvent): void {
    if (!this.longPress) {
      return;
    }

    if (!this.isPointerPressActive) {
      return;
    }

    if (this.dragStartedFromCurrentPress) {
      return;
    }

    if (this.pointerStartCoordinateX === null || this.pointerStartCoordinateY === null) {
      return;
    }

    const deltaX = pointerEvent.clientX - this.pointerStartCoordinateX;
    const deltaY = pointerEvent.clientY - this.pointerStartCoordinateY;
    const hasMovedBeyondThreshold =
      Math.abs(deltaX) > this.movementThresholdPixels ||
      Math.abs(deltaY) > this.movementThresholdPixels;

    if (!hasMovedBeyondThreshold) {
      return;
    }

    this.clearDragDelayTimer();
    this.startDrag();
  }

  /**
   * Handles pointerup and pointercancel events on the host element.
   * Finalizes the drag session and emits `openRequested` if applicable.
   */
  @HostListener("pointerup", ["$event"])
  @HostListener("pointercancel", ["$event"])
  protected onPointerUp(pointerEvent: PointerEvent): void {
    const pointerTargetElement = pointerEvent.currentTarget as HTMLElement | null;
    if (pointerTargetElement?.hasPointerCapture(pointerEvent.pointerId)) {
      pointerTargetElement.releasePointerCapture(pointerEvent.pointerId);
    }

    if (this.longPress) {
      const shouldOpenChat = this.isPointerPressActive && !this.dragStartedFromCurrentPress;
      this.clearDragDelayTimer();
      this.isPointerPressActive = false;

      if (shouldOpenChat) {
        this.openRequested.emit();
        this.resetDragState();
        return;
      }
    }

    this.resetDragState();
  }

  /**
   * Starts a drag session by setting the dragging state, releasing pointer capture so the
   * host overlay can receive pointer events, and notifying the parent service.
   * This method is called when a drag is initiated, either immediately or after a long-press delay.
   */
  private startDrag(): void {
    if (this.hostService.isSmallScreen()) {
      return;
    }

    if (this.dragStartedFromCurrentPress) {
      return;
    }

    this.dragStartedFromCurrentPress = true;
    this._isDragging.set(true);

    if (this.activePointerId !== null) {
      const nativeElement = this.elementRef.nativeElement as HTMLElement;
      if (nativeElement.hasPointerCapture(this.activePointerId)) {
        nativeElement.releasePointerCapture(this.activePointerId);
      }
    }

    this.appParentService.sendDragStart();
  }

  /**
   * Resets the drag state entirely — dragging flag, long-press state, and pointer tracking.
   * This method is called when a drag session ends, is canceled, or the host reports `dragEnd$`.
   */
  private resetDragState(): void {
    this._isDragging.set(false);
    this.dragStartedFromCurrentPress = false;
    this.activePointerId = null;
    this.isPointerPressActive = false;
    this.pointerStartCoordinateX = null;
    this.pointerStartCoordinateY = null;
    this.clearDragDelayTimer();
  }

  /**
   * Clears the long-press drag delay timer if it is active.
   * This method is called when a drag session is finalized or canceled to prevent the timer from triggering a drag start after the session has ended.
   */
  private clearDragDelayTimer(): void {
    if (this.dragDelayTimer === null) {
      return;
    }

    clearTimeout(this.dragDelayTimer);
    this.dragDelayTimer = null;
  }
}
